import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import { useMapFullscreen } from "../../context/MapFullscreenContext";
import SinConexionKawaii from "../SinConexionKawaii";

// Template153: Día 1 - Mapa Île de la Cité + Barrio Latino + Luxemburgo. Todo desde backend.
// page.texts[0]=header, [1]=burbuja, [2]=texto inferior | page.maps[0]=url | page.canvasImages (logo, sticker)
const Template153 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [loaded, setLoaded] = useState(false);
	const [fullscreen, setFullscreen] = useState(false);
	const [isOnline, setIsOnline] = useState(true);
	const [stickerError, setStickerError] = useState(false);
	const { setMapFullscreen } = useMapFullscreen() || {};

	useEffect(() => {
		if (typeof setMapFullscreen === "function") {
			setMapFullscreen(fullscreen);
			return () => setMapFullscreen(false);
		}
	}, [fullscreen, setMapFullscreen]);

	useEffect(() => {
		if (typeof navigator !== "undefined") {
			setIsOnline(navigator.onLine);
			setLoaded(navigator.onLine);
		}
		const handleOnline = () => {
			setIsOnline(true);
			setLoaded(true);
		};
		const handleOffline = () => {
			setIsOnline(false);
			setLoaded(true);
		};
		window.addEventListener("online", handleOnline);
		window.addEventListener("offline", handleOffline);
		return () => {
			window.removeEventListener("online", handleOnline);
			window.removeEventListener("offline", handleOffline);
		};
	}, []);

	const getCanvasImageUrl = (id) => {
		if (page?.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const headerText = page?.texts?.[0]?.content;
	const speechBubbleText = page?.texts?.[1]?.content;
	const bottomText = page?.texts?.[2]?.content;
	const logoImage = getCanvasImageUrl("logo");
	const stickerImage = getCanvasImageUrl("sticker");
	const mapUrl = page?.maps?.[0];

	const actualHeaderColor = headerBackgroundColor || "#2C3E50";
	const contentColor = textColor || "#444";

	if (fullscreen && mapUrl) {
		return (
			<Box
				sx={{
					position: "fixed",
					top: 0,
					left: 0,
					right: 0,
					bottom: 0,
					backgroundColor: "#fff",
					zIndex: 9999,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Box
					sx={{
						padding: "10px",
						textAlign: "right",
						backgroundColor: actualHeaderColor,
					}}
				>
					<Box
						component="button"
						onClick={() => setFullscreen(false)}
						sx={{
							backgroundColor: "#fff",
							color: actualHeaderColor,
							border: "none",
							borderRadius: "8px",
							padding: "6px 12px",
							fontWeight: 600,
							cursor: "pointer",
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						}}
					>
						← Volver a la guía
					</Box>
				</Box>
				<iframe
					title="Mapa grande"
					src={mapUrl}
					width="100%"
					height="100%"
					style={{ border: 0, flex: 1 }}
					allowFullScreen
					loading="lazy"
				/>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						fontSize: "14px",
						fontWeight: 600,
						flexShrink: 0,
						zIndex: 99999,
					}}
				>
					{headerText}
					{!isSmallScreen && logoImage && (
						<img
							src={logoImage}
							alt=""
							style={{
								width: 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			<Box
				data-block-id="map"
				sx={{
					flex: 1,
					marginTop: headerText ? "-70px" : 0,
					position: "relative",
				}}
			>
				{isOnline && mapUrl && !loaded && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							height: "100%",
							position: "absolute",
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							zIndex: 1,
						}}
					>
						<Box
							sx={{
								width: 20,
								height: 20,
								border: "4px solid #ccc",
								borderTop: `4px solid ${actualHeaderColor}`,
								borderRadius: "50%",
								animation: "spin 1s linear infinite",
								"@keyframes spin": {
									"0%": { transform: "rotate(0deg)" },
									"100%": { transform: "rotate(360deg)" },
								},
							}}
						/>
					</Box>
				)}

				{isOnline && mapUrl && (
					<Box
						component="button"
						onClick={() => setFullscreen(true)}
						sx={{
							position: "absolute",
							top: 80,
							right: 10,
							backgroundColor: actualHeaderColor,
							color: "#fff!important",
							padding: "8px 10px",
							fontSize: "11px",
							borderRadius: "8px",
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
							boxShadow: "1px 1px 2px rgba(0,0,0,0.2)",
							zIndex: 4,
							border: "none",
							cursor: "pointer",
						}}
					>
						Ver mapa grande
					</Box>
				)}

				{isOnline && mapUrl ? (
					<iframe
						title="Mapa Día 1"
						src={mapUrl}
						width="100%"
						height="100%"
						style={{ border: 0 }}
						onLoad={() => setLoaded(true)}
					/>
				) : !isOnline ? (
					<SinConexionKawaii height="100%" fontFamily={fontFamily} />
				) : !mapUrl ? (
					<Box
						sx={{
							width: "100%",
							height: "100%",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							backgroundColor: "#E8F4F8",
						}}
					>
						<Box
							component="p"
							sx={{
								fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
								color: "#666",
							}}
						>
							Mapa no configurado
						</Box>
					</Box>
				) : null}

				{stickerImage && !stickerError && (
					<img
						src={stickerImage}
						alt=""
						onError={() => setStickerError(true)}
						style={{ width: 120, position: "absolute", bottom: -20, left: -18 }}
					/>
				)}

				{speechBubbleText && (
					<Box
						sx={{
							position: "absolute",
							bottom: 60,
							left: 60,
							backgroundColor: "#fff8db",
							border: "2px solid #f5c400",
							borderRadius: "16px 16px 16px 0px",
							padding: "10px 12px",
							fontSize: 11,
							fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
							color: contentColor,
							boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
							maxWidth: 180,
						}}
					>
						{speechBubbleText}
					</Box>
				)}
			</Box>

			{bottomText && (
				<Box
					data-block-id="text-1"
					sx={{
						padding: "12px 16px",
						fontFamily: `${fontFamily || "'Poppins', sans-serif"}`,
						fontSize: 13,
						color: "#333",
					}}
				>
					{bottomText}
				</Box>
			)}
		</Box>
	);
};

Template153.thumbnail = "/images/template-1.png";

export default Template153;
